import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DashboardOutlined, TeamOutlined, StopOutlined, HomeOutlined, SearchOutlined,
  BellOutlined, MessageOutlined, MailOutlined, GlobalOutlined, BankOutlined,
  UserSwitchOutlined, TagOutlined, SettingOutlined, FlagOutlined, EnvironmentOutlined,
  CompassOutlined, AimOutlined, FileSearchOutlined, AlertOutlined, CloudDownloadOutlined,
  StarFilled, WhatsAppOutlined,
} from '@/shared/ui/icons';

import type { ReactNode } from 'react';
type Item = { key: string; label: string; icon?: ReactNode };
type Group = { key: string; label: string; items: Item[] };

export default function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const groups: Group[] = useMemo(
    () => [
      {
        key: 'overview', label: t('sidebar.overview'),
        items: [
          { key: '/', label: t('sidebar.dashboard'), icon: <DashboardOutlined /> },
          { key: '/reports', label: t('sidebar.reportsQueue'), icon: <AlertOutlined /> },
          { key: '/audit-logs', label: t('sidebar.auditLog'), icon: <FileSearchOutlined /> },
          { key: '/imported-ads', label: t('sidebar.importedAds'), icon: <CloudDownloadOutlined /> },
          { key: '/ai-create', label: t('sidebar.aiCreate'), icon: <StarFilled /> },
          { key: '/ai-whatsapp-updates', label: t('sidebar.aiWhatsappUpdates'), icon: <WhatsAppOutlined /> },
          { key: '/blog-posts', label: t('sidebar.blogPosts'), icon: <FileSearchOutlined /> },
        ],
      },
      {
        key: 'access', label: t('sidebar.adminAccess'),
        items: [
          { key: '/admin-users', label: t('sidebar.adminUsers'), icon: <UserSwitchOutlined /> },
          { key: '/roles', label: t('sidebar.roles'), icon: <SettingOutlined /> },
          { key: '/permissions', label: t('sidebar.permissions'), icon: <FileSearchOutlined /> },
        ],
      },
      {
        key: 'analytics', label: t('sidebar.analytics'),
        items: [
          { key: '/analytics/growth', label: t('sidebar.growthAnalytics'), icon: <DashboardOutlined /> },
          { key: '/analytics/demand-intelligence', label: t('sidebar.demandIntelligence'), icon: <AimOutlined /> },
          { key: '/analytics/marketplace', label: t('sidebar.marketplaceAnalytics'), icon: <HomeOutlined /> },
          { key: '/analytics/search', label: t('sidebar.searchAnalytics'), icon: <SearchOutlined /> },
          { key: '/analytics/funnels', label: t('sidebar.funnelAnalytics'), icon: <AimOutlined /> },
          { key: '/analytics/retention', label: t('sidebar.retentionAnalytics'), icon: <TeamOutlined /> },
          { key: '/analytics/attribution', label: t('sidebar.attributionAnalytics'), icon: <GlobalOutlined /> },
          { key: '/analytics/events', label: t('sidebar.analyticsEvents'), icon: <FileSearchOutlined /> },
        ],
      },
      {
        key: 'users', label: t('sidebar.users_section'),
        items: [
          { key: '/users', label: t('sidebar.users'), icon: <TeamOutlined /> },
          { key: '/blocked-users', label: t('sidebar.blockedUsers'), icon: <StopOutlined /> },
        ],
      },
      {
        key: 'ads', label: t('sidebar.ads'),
        items: [
          { key: '/room-ads', label: t('sidebar.roomAds'), icon: <HomeOutlined /> },
          { key: '/room-wanted', label: t('sidebar.roomWanted'), icon: <SearchOutlined /> },
        ],
      },
      {
        key: 'comm', label: t('sidebar.communication'),
        items: [
          { key: '/notifications', label: t('sidebar.notifications'), icon: <BellOutlined /> },
          { key: '/chat/rooms', label: t('sidebar.chatRooms'), icon: <MessageOutlined /> },
          { key: '/chat/messages', label: t('sidebar.messages'), icon: <MailOutlined /> },
        ],
      },
      {
        key: 'system', label: t('sidebar.system'),
        items: [
          { key: '/nationalities', label: t('sidebar.nationalities'), icon: <GlobalOutlined /> },
          { key: '/housing-statuses', label: t('sidebar.housingStatuses'), icon: <BankOutlined /> },
          { key: '/referrals', label: t('sidebar.referrals'), icon: <UserSwitchOutlined /> },
          { key: '/property-amenities', label: t('sidebar.amenities'), icon: <TagOutlined /> },
          { key: '/system-settings', label: t('sidebar.settings'), icon: <SettingOutlined /> },
          { key: '/admin/translations', label: t('sidebar.translations'), icon: <GlobalOutlined /> },
        ],
      },
      {
        key: 'geo', label: t('sidebar.geography'),
        items: [
          { key: '/countries', label: t('sidebar.countries'), icon: <FlagOutlined /> },
          { key: '/regions', label: t('sidebar.regions'), icon: <CompassOutlined /> },
          { key: '/cities', label: t('sidebar.cities'), icon: <EnvironmentOutlined /> },
          { key: '/districts', label: t('sidebar.districts'), icon: <AimOutlined /> },
        ],
      },
    ],
    [t],
  );

  const selectedKey = useMemo(() => {
    const path = location.pathname;
    if (path === '/') return '/';
    const allKeys = groups.flatMap((g) => g.items.map((i) => i.key));
    return allKeys.filter((k) => k !== '/' && path.startsWith(k)).sort((a, b) => b.length - a.length)[0] || '/';
  }, [location.pathname, groups]);

  return (
    <aside className="ds-sidebar">
      <div className="ds-brand">
        <div className="ds-brand-mark">ش</div>
        <div>
          <div className="ds-brand-name">{t('app.name')}</div>
          <div className="ds-brand-sub">{t('app.admin')}</div>
        </div>
      </div>

      <nav className="ds-menu" style={{ paddingBottom: 24 }}>
        {groups.map((group) => (
          <div key={group.key}>
            <div className="ds-menu-group-title">{group.label}</div>
            {group.items.map((item) => (
              <div
                key={item.key}
                className={`ds-menu-item${item.key === selectedKey ? ' active' : ''}`}
                onClick={() => navigate(item.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(item.key); }}
              >
                {item.icon}
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        ))}
      </nav>
    </aside>
  );
}
